import moment from "moment";
import { createSelector } from "reselect";
import { dateToMySQLFormat } from "~/utils/date.jsx";
import { descSortCompare, sortCompare } from "~/utils/utils.jsx";
import { getFilterCalendarConfigOption } from "./selector.configOptions.jsx";
import { getAllTasks } from "./selector.tasks.jsx";

const getFirstArg = (state, id) => id;

export const getSubtasks = createSelector(
  (state) => state.subtasks,
  (subtasks) => subtasks
);
export const getSubtaskById = createSelector(getSubtasks, getFirstArg, (subtasks, id) => {
  return subtasks[`${id}`];
});

export const getSubtasksArray = createSelector(getSubtasks, (subtasks) => {
  if (subtasks) {
    return Object?.values(subtasks);
  }
  return [];
});

export const getSubtasksByTaskId = createSelector(getSubtasksArray, getFirstArg, (subtasks, taskId) => {
  return subtasks.filter((subtask) => subtask.task_id === taskId).sort((a, b) => sortCompare(a.position, b.position));
});

export const getSubtasksIdsByTaskId = createSelector(getSubtasksByTaskId, (subtasks) => {
  return subtasks.map((s) => s.id);
});

export const getActiveSubtasksIdsByTaskId = createSelector(getSubtasksByTaskId, (subtasks) => {
  return subtasks.filter((subtask) => !subtask.is_trashed).map((s) => s.id);
});

export const getNumOfOpenSubtasksByTaskId = createSelector(getSubtasksByTaskId, (subtasks) => {
  const result = subtasks.reduce((prev, next) => {
    if (!next.is_trashed && !next.is_completed) {
      prev++;
    }
    return prev;
  }, 0);
  return result;
});

export const getSubtasksNameAndAssigneeIdByTaskId = createSelector(getSubtasksByTaskId, (subtasks) => {
  return subtasks.map((s) => {
    return {
      body: s.name,
      assignee_id: s.assignee_id,
      id: s.id,
    };
  });
});

export const getAllActiveSubtasks = createSelector(getSubtasksArray, (subtasks) => {
  return subtasks.filter((subtask) => !subtask.is_trashed);
});

export const getOpenSubtasks = createSelector(getSubtasksArray, (subtasks) => {
  return subtasks.filter((subtask) => !subtask.is_trashed && !subtask.is_completed);
});

export const getOpenSubtasksByTaskId = createSelector(getSubtasksByTaskId, (subtasks) => {
  return subtasks.reduce((acc, subtask) => {
    if (!subtask.is_completed && !subtask.is_trashed) {
      acc.push(subtask.id);
    }

    return acc;
  }, []);
});

export const getIsSubtaskCompleted = createSelector(getAllTasks, getSubtaskById, (allTasks, subtask) => {
  if (!subtask) return false;
  const taskId = subtask.task_id;
  const task = allTasks.get(`${taskId}`);
  if (task && task.get("is_completed")) {
    return true;
  }
  return subtask.is_completed;
});

export const getCompletedSubtaskIdsByTaskId = createSelector(getSubtasksByTaskId, getAllTasks, (subtasks, tasks) => {
  const completedSubtasks = subtasks.reduce((acc, item) => {
    const taskId = item?.task_id;
    const task = tasks.get(`${taskId}`);
    if (((item && item.is_completed) || (task && task.is_completed)) && !item.is_trashed) {
      acc.push({ id: item.id, completedOn: item.completed_on });
    }
    return acc;
  }, []);

  return completedSubtasks.sort((a, b) => descSortCompare(a.completedOn, b.completedOn)).map((subtask) => subtask.id);
});

export const getOpenSubtasksCountByTaskId = createSelector(getOpenSubtasksByTaskId, (subtasks) => {
  return subtasks.length;
});

export const getSubtasksForGantt = createSelector(
  getAllActiveSubtasks,
  getAllTasks,
  (state) => state.task_lists,
  (state, projectId) => projectId,
  (activeSubtasks, tasks, taskLists, projectId) => {
    return activeSubtasks
      .filter((sub) => {
        return sub.project_id === projectId && sub?.due_on > 0 && tasks?.get(`${sub.task_id}`);
      })
      .map((sub) => {
        const task = tasks.get(`${sub.task_id}`);
        const taskList = taskLists.get(`${task.task_list_id}`);
        return {
          id: `Subtask-${sub.id}`,
          text: sub.name,
          class: "Subtask",
          original_id: sub.id,
          list_id: task?.get(`task_list_id`),
          sortKey: `${taskList.get("position")}-${task?.get(`task_list_id`)}-${task?.position}-${sub.position}`,
          type: sub.class,
          projectId: sub.project_id,
          start_date: dateToMySQLFormat(sub.due_on, `DD-MM-YYYY`),
          duration: 1,
          taskList: `task-list-${task?.get(`task_list_id`)}`,
          assignee_id: sub.asignee_id,
          labels: [],
          due_on: sub.due_on,
          updated_on: sub.updated_on,
          is_in_conflict: task?.get(`due_on`) ? sub.due_on > task?.get(`due_on`) : false,
          is_completed: sub?.is_completed || task?.is_completed,
          task_id: sub.task_id,
          loading_due_date_reschedule: sub?.loading_due_date_reschedule ? sub?.loading_due_date_reschedule : false,
          parent: sub.task_id,
        };
      });
  }
);

export const getSubTasksByDate = createSelector(
  getOpenSubtasks,
  getFilterCalendarConfigOption,
  (state, date) => moment.utc(date).startOf("day"),
  (subtasks, filters, date) => {
    let assigneeIds = filters?.user || [];
    let projectIds = filters?.project || [];

    if (assigneeIds.length === 0) {
      assigneeIds = undefined;
    }
    if (projectIds.length === 0) {
      projectIds = undefined;
    }

    const result = subtasks.reduce((prev, next) => {
      const dueDate = moment.unix(next.due_on).startOf("day");
      const isInDate = dueDate.isSame(date, "day");
      let flag = false;

      if (assigneeIds && projectIds) {
        flag = isInDate && assigneeIds.includes(next.assignee_id) && projectIds.includes(next.project_id);
      } else if (assigneeIds && !projectIds) {
        flag = isInDate && assigneeIds.includes(next.assignee_id);
      } else if (!assigneeIds && projectIds) {
        flag = isInDate && projectIds.includes(next.project_id);
      } else {
        flag = isInDate;
      }
      if (flag) {
        prev.push(next);
      }
      return prev;
    }, []);

    return result;
  }
);

export const getTrashedSubtaskIds = createSelector(
  getSubtasksArray,
  (state, projectId) => projectId,
  (subtasks, projectId) => subtasks.filter((s) => s.project_id === projectId && s.is_trashed).map((s) => s.id)
);
